import React, { useState } from 'react';
import { Input } from '@/components/atoms/Input';
import { Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import { UserSearchResults } from '../UserSearchResults';
import { RecentSearches } from '../RecentSearches';
import { useUserSearch } from '@/hooks/useUserSearch';
import { useRecentSearches } from '@/hooks/useRecentSearches';

export const HeaderSearch: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const { users, isLoading } = useUserSearch(searchQuery);
  const { recentSearches, isLoading: isLoadingRecent, addSearch } = useRecentSearches(5);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsOpen(true); // Always open popover when typing
  };

  const handleSelect = (userId?: string) => {
    // Add to search history if user clicked on a search result
    if (userId) {
      addSearch(userId);
    }
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleInteractOutside = (e: Event) => {
    // Prevent popover from closing when clicking on the input or its container
    const target = e.target as HTMLElement;
    if (target.closest('[data-search-input]')) {
      e.preventDefault();
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl shadow-none px-2 transition-all duration-300 h-12 flex items-center flex-1">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverAnchor asChild>
          <div className="relative w-full" data-search-input>
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-foreground h-6 w-6" />
            <Input 
              placeholder="Search users..." 
              className="pl-10 bg-transparent border-none focus-visible:ring-0 focus-visible:ring-offset-0 text-base text-foreground placeholder:text-foreground"
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={() => setIsOpen(true)}
            />
          </div>
        </PopoverAnchor>
        <PopoverContent 
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={handleInteractOutside}
        >
          {searchQuery.trim() ? (
            <UserSearchResults 
              users={users} 
              isLoading={isLoading}
              onSelect={handleSelect}
            />
          ) : (
            <RecentSearches 
              searches={recentSearches}
              isLoading={isLoadingRecent}
              onSelect={handleSelect}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};
